import type { EventRegistrationConfig } from '@pulsify/shared/dto'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { registrationsApi } from '~/entities/event/api/registrations-api'
import { QueryKey, queryKey } from '~/shared/lib/query-keys'

export function useEventRegistrationSettings(businessId: string, eventId: string) {
  return useQuery({
    queryKey: queryKey(QueryKey.EVENT_REGISTRATION, eventId),
    queryFn: () => registrationsApi.settings(businessId, eventId),
    enabled: !!businessId && !!eventId,
  })
}

export function useEventRegistrations(businessId: string, eventId: string, enabled: boolean) {
  return useQuery({
    queryKey: queryKey(QueryKey.EVENT_REGISTRATIONS, eventId),
    queryFn: () => registrationsApi.list(businessId, eventId),
    enabled: enabled && !!businessId && !!eventId,
  })
}

function useRefreshRegistrations(eventId: string) {
  const queryClient = useQueryClient()
  return () => {
    queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.EVENT_REGISTRATION, eventId) })
    queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.EVENT_REGISTRATIONS, eventId) })
    queryClient.invalidateQueries({ queryKey: queryKey(QueryKey.EVENT, eventId) })
  }
}

export function useSaveEventRegistration(businessId: string, eventId: string) {
  const refresh = useRefreshRegistrations(eventId)
  return useMutation({
    mutationFn: (config: EventRegistrationConfig) =>
      registrationsApi.save(businessId, eventId, config),
    onSuccess: refresh,
  })
}

export function useCancelEventRegistration(businessId: string, eventId: string) {
  const refresh = useRefreshRegistrations(eventId)
  return useMutation({
    mutationFn: (registrationId: string) =>
      registrationsApi.cancel(businessId, eventId, registrationId),
    onSuccess: refresh,
  })
}

export function useExportEventRegistrations(businessId: string, eventId: string) {
  return useMutation({
    mutationFn: () => registrationsApi.exportCsv(businessId, eventId),
  })
}
